import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?q=80&w=1600&auto=format&fit=crop";

const LINES = ["Great prices", "for the quality."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden pt-24">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-10 px-6 pb-16 sm:px-10 lg:grid-cols-12 lg:gap-0">
        <div className="flex flex-col justify-center lg:col-span-6 lg:pr-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-8 text-xs uppercase tracking-[0.3em] text-taupe"
          >
            Home decor — delivered across New York, by hand
          </motion.p>
          <h1 className="font-serif text-5xl font-light leading-[0.95] tracking-tighter sm:text-6xl lg:text-8xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.18, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-md text-base font-light leading-relaxed text-ash"
          >
            Furniture, lighting and objects for people who notice the difference —
            priced for people who shouldn't have to pay for it.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-12 flex items-center gap-6"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-cta"
              className="group flex items-center gap-3 rounded-full bg-charcoal px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss"
            >
              Shop the collection
              <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </Link>
          </motion.div>
        </div>
        <div className="relative lg:col-span-6">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.4, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-[60vh] overflow-hidden lg:h-[80vh]"
          >
            <motion.img
              src={HERO_IMAGE}
              alt="A warm, minimal living room styled with Hearth & Co. pieces"
              style={{ y, scale }}
              className="h-[120%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-charcoal/10" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-4 text-xs uppercase tracking-[0.2em] text-ash"
          >
            The Autumn Room — styled entirely from this season's collection
          </motion.p>
        </div>
      </div>
    </section>
  );
};
